import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { RouteComponentProps } from 'react-router';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { isEmpty } from 'lodash';
import 'babel-polyfill';
import Modal from 'react-modal';
import moment from 'moment-timezone';

import { MULTIPLE_PROVIDER_QUOTES } from '../constants';
import { ButtonLink } from '../../../components/Link';
import NoResults from '../components/NoResults';
import Pagination from '../../../components/Pagination';
import Provider from '../components/Provider';
import Section from '../../../components/Section';
import { DeltaHeading } from '../../../components/Typography';
import calculateRem from '../../../styles/calculateRem';
import mediaQueries from '../../../styles/mediaQueries';
import { colors } from '../../../styles/constants';
import { postcodeDetailText } from '../../../util/transforms';
import { createProviderListPaginationSubheading } from '../../../util/pagination';
import { AgeGroup, FundingType, Provider as ProviderT, Query, Service } from '../../../state/types';
import { ProvidersActions } from '../actions';
import { clearProvidersToQuote, ProviderSearchOrder, setProviderSearchOrder, setProviderSearchPage, toggleProviderToQuote } from "../providersSlice";
import { useAppDispatch, useAppSelector } from '../../../state/hooks';
import karistaApi from "../../../services/karista";

import {
  Card,
  CardInfoText,
  CallLink,
  ModalSection,
  CloseButton,
  modalStyles
} from './styles'

import callIcon from './static/icon-phone.png';
import chatIcon from './static/icon-livechat.png';
import bannerImage from './static/list-banner.svg'
import bannerImageMobile from './static/list-banner-mobile.svg'

const StyledSection = styled(Section)`
  padding-top: ${calculateRem(48)};
`;

// TODO change to Single line paragraph
const ProviderCount = styled(DeltaHeading)`
  font-weight: 300;
`;

const CancelButton = styled.p`
  text-decoration: underline;
  cursor: pointer;
  margin-top: 12px;
`;

const QuoteDialog = styled.div`
  bottom: 0;
  left: 0;
  right: 0;
  position: fixed;
  background-color: ${colors.purple};
  color: ${colors.white};
  width: 100%;
`;

const QuoteDialogContainer = styled.section`
  margin: 0 auto;
  max-width: 720px;
  padding: 24px 24px 48px;
  display: flex;
  justify-content: space-between;
  ${mediaQueries.mobile`
    display: block;
    padding-bottom: 24px;
  `};
`;

const QuoteDialogActions = styled.aside`
  text-align: center;
  ${mediaQueries.mobile`
    margin-top: 24px;
  `};
`;

const ResultsSortSelectContainer = styled.div`
  text-align: end;
  margin-bottom: 1rem;
  ${mediaQueries.mobile`
    text-align: start;
  `};
`;

const ResultsSortSelectLabel = styled.label`
  font-size: 1rem;
`;

const ResultsSortSelect = styled.select`
  color: ${colors.darkPurple};
  padding: 2px;
  border: 1px solid ${colors.darkPurple};
  border-radius: 3px;
  font-size: 1rem;
  font-family: filson-soft, sans-serif;
  font-weight: 300;
`;

const iconStyle = {
  width: '20px',
  height: '20px',
};

const BannerImage = styled.img`
  width: 100%;
  height: auto;

  ${mediaQueries.mobile`
    max-width: 100%;
    height: auto;
  `}
`;

export interface ListProps extends RouteComponentProps {
  services?: Service[],
  ageGroups?: {[k: string]: AgeGroup},
  fundingTypes?: {[k: string]: FundingType},
  actions: ProvidersActions,
  loading: boolean,
  pageSize?: number
};

const List: React.FC<ListProps> = props => {
  const {
    actions,
    loading,
    pageSize=20,
    history,
    ageGroups,
    fundingTypes,
  } = props;

  const dispatch = useAppDispatch();
  const {
    query,
    page = 1,
    order = "highly_reviewed",
    filters,
    providersToQuote,
  } = useAppSelector(state => state.providers.providerSearchParams);

  const { data, isLoading: areProvidersLoading } = karistaApi.useGetProvidersQuery(
    { pageSize, query, page, ...filters, order },
  );

  const canSelectProviders = !!filters.service_areas_list && MULTIPLE_PROVIDER_QUOTES;
  const isQuoteDialogEnabled = (
    providersToQuote.length > 0
    && !!filters.service_areas_list
    && MULTIPLE_PROVIDER_QUOTES
  );

  const isLoading = (
    areProvidersLoading
    && !isEmpty(props.services)
    && !isEmpty(props.ageGroups)
    && !isEmpty(props.fundingTypes)
  );

  // State to manage the visibility of the help popup
  const [showHelpPopup, setShowHelpPopup] = useState(false);

  useEffect(() => {
    // Get the start of the current day in Melbourne time zone
    const startOfDay = moment().tz('Australia/Melbourne').startOf('day');

    const businessHoursStart = startOfDay.clone().set({ hour: 9, minute: 0 }).valueOf(); // 9:00 AM in Melbourne time
    const businessHoursEnd = startOfDay.clone().set({ hour: 17, minute: 30 }).valueOf(); // 5:30 PM in Melbourne time

    // Get current time in Melbourne time zone
    const currentTime = moment().tz('Australia/Melbourne');
    const dayOfWeek = currentTime.day();
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
    const currentTimeValue = currentTime.valueOf();


    // Check if current time falls within business hours
    const isWithinBusinessHours = isWeekday && (currentTimeValue >= businessHoursStart && currentTimeValue <= businessHoursEnd);

    // Calculate remaining time until the end of business hours
    let remainingTime = 0;
    if (isWithinBusinessHours) {
      remainingTime = businessHoursEnd - currentTimeValue;
    }

    // Set a timeout to show the help popup after 25 seconds
    const timeout = setTimeout(() => {
      if (remainingTime <= 0) {
        setShowHelpPopup(false);
      } else {
        setShowHelpPopup(true);
      }
    }, 25000);
  
    // Clear the timeout on component unmount
    return () => clearTimeout(timeout);
  }, []);

  const closePopup = () => {
    // Close the popup
    setShowHelpPopup(false);
  };

  if (isLoading) {
    // TODO: Make a common loading component. This could be done when suspense is added.
    return <div>Loading providers ...</div>;
  }
  if (!data || !data.count) {
    return <NoResults actions={actions} />;
  }

  const openIntercom = e => {
    e.preventDefault();
    (window as any).Intercom(
      'showNewMessage',
      `Hi there, how can we help`
    );
  };

  return (
    <>
    {window.innerWidth > 768 ? (
        <BannerImage src={bannerImage} alt="Karista can connect you to providers with availability" />
    ) : (
        <BannerImage src={bannerImageMobile} alt="Karista can connect you to providers with availability" />
    )}
    <StyledSection>
      {showHelpPopup && (<Modal
        isOpen={showHelpPopup}
        onRequestClose={closePopup}
        contentLabel="Wait time Popup"
        style={modalStyles}
      >
        <CloseButton onClick={closePopup}>x</CloseButton>
         <ModalSection>
          <p>
          Karista's Client Services 
          team are experienced in finding and connecting NDIS and HCP participants 
          to services based on your personal needs and goals. Speak to our Client Services Team now.
          </p>
          <br></br>
          <Card>
            <img src={callIcon} alt="Call icon" style={iconStyle}/>
            <CallLink href="tel:+611300274782">
              <CardInfoText>Call us on 1300 274 782</CardInfoText>
            </CallLink>
          </Card>
            <br/>
          <Card onClick={openIntercom}>
            <img src={chatIcon} alt="Chat icon" style={iconStyle} />
            <CardInfoText>Live Chat</CardInfoText>
          </Card>
           </ModalSection>
      </Modal>)}
      <ProviderCount>
        <Subheading
          filters={filters}
          pagination={{current: page, ...data}}
          ageGroups={ageGroups}
          fundingTypes={fundingTypes}
        />
      </ProviderCount>
      <ResultsSortSelectContainer>
        <ResultsSortSelectLabel>Sort by: </ResultsSortSelectLabel>
        <ResultsSortSelect
          name="results_order"
          onChange={e => dispatch(setProviderSearchOrder(e.target.value as ProviderSearchOrder))}
        >
          <option value="highly_reviewed" selected={order !== "by_score"}>Highly reviewed</option>
          <option value="by_score" selected={order === "by_score"}>Top rated</option>
        </ResultsSortSelect>
      </ResultsSortSelectContainer>
      {loading || (
        data.results.map(elem => (
          <Provider
            key={elem.id}
            {...elem}
            url_slug={elem.url_slug}
            location={props.location}
            history={history}
            match={props.match}
            canSelect={canSelectProviders}
            providersToQuote={providersToQuote}
            onProviderSelect={providerId => dispatch(toggleProviderToQuote(providerId))}
          />
        ))
      )}
      {data.totalPages > 1 && (
        <Pagination
          onClick={page => dispatch(setProviderSearchPage(page))}
          current={page}
          {...data}
        />
      )}
      {isQuoteDialogEnabled && (
        <QuoteDialog>
          <QuoteDialogContainer>
            <p>
              Please click <b>Next</b> so we can collect information confidentially, <br/>
              to ensure you are matched correctly
            </p>
            <QuoteDialogActions>
              <ButtonLink
                border
                to="/providers/request-quote"
              >
                Next
              </ButtonLink>
              <CancelButton onClick={() => dispatch(clearProvidersToQuote())}>
                cancel selections
              </CancelButton>
            </QuoteDialogActions>
          </QuoteDialogContainer>
        </QuoteDialog>
      )}
    </StyledSection>
    </>
  );
}

interface SubheadingProps {
  pagination: {
    current: number,
    pageSize: number,
    count: number,
  },
  filters: {[key: string]: string},
  ageGroups?: {[key: string]: AgeGroup},
  fundingTypes?: {[key: string]: FundingType},
};

function Subheading(props: SubheadingProps) {
  const { pagination, filters, ageGroups, fundingTypes } = props;
  const subheading = createProviderListPaginationSubheading(
    pagination.current,
    pagination.pageSize,
    pagination.count,
  );

  const { data: postcodesDetail } = karistaApi.useGetPostcodeQuery(
    !!filters.postcodes_list ? filters.postcodes_list : skipToken,
  );

  const serviceName = filters.service_areas_list;
  const ageDescription = (
    (ageGroups && filters.age_groups_list)
    ? ageGroups[filters.age_groups_list].description
    : null
  );
  const fundingName = (
    (fundingTypes && filters.funding_types_list)
    ? fundingTypes[filters.funding_types_list].name
    : null
  );

  return (
    <>
      {subheading}
      {(serviceName || ageDescription || fundingName) && ' for'}
      {serviceName && ` ${serviceName}`}
      {ageDescription &&
        (serviceName ? `, ${ageDescription}` : ` ${ageDescription}`)}
      {fundingName &&
        (serviceName || ageDescription
          ? `, ${fundingName}`
          : ` ${fundingName}`)}
      {postcodeDetailText(postcodesDetail)}.
      {serviceName && fundingName && ageDescription ? (
          <> 
              <b> Select one or more providers</b> to move forward.
          </>
      ) : (
          ""
      )}
    </>
  );
}

export default List;
